'use client';

import * as React from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { MailIcon, PhoneIcon } from 'lucide-react';
import { useAction } from 'next-safe-action/hooks';
import { useForm } from 'react-hook-form';

import { Button } from '@workspace/ui/components/button';
import { Card, CardContent } from '@workspace/ui/components/card';
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormProvider
} from '@workspace/ui/components/form';
import { Input } from '@workspace/ui/components/input';
import { toast } from '@workspace/ui/components/sonner';
import { Textarea } from '@workspace/ui/components/textarea';

import { sendContactMessage } from '~/actions/send-contact-message';
import { GridSection } from '~/components/fragments/grid-section';
import { SiteHeading } from '~/components/fragments/site-heading';
import { contactSchema, type ContactSchema } from '~/schemas/contact-schema';

export function Contact(): React.JSX.Element {
  const form = useForm<ContactSchema>({
    resolver: zodResolver(contactSchema),
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      message: ''
    }
  });

  const { execute, isExecuting } = useAction(sendContactMessage, {
    onSuccess: () => {
      toast.success(
        'Bedankt voor je bericht! We nemen binnen 48 uur contact met je op.'
      );
      form.reset();
    },
    onError: ({ error }) => {
      toast.error(
        error.serverError || 'Er ging iets mis. Probeer het later opnieuw.'
      );
    }
  });

  const handleSendMessage = (data: ContactSchema): void => {
    execute(data);
  };

  return (
    <GridSection>
      <div className="container space-y-20 py-20">
        <SiteHeading
          badge="Contact"
          title={
            <>
              We horen graag
              <br /> van jou!
            </>
          }
        />
        <div className="lg:container lg:max-w-6xl ">
          <div className="flex flex-col justify-between gap-10 lg:flex-row lg:gap-20">
            <div className="order-2 space-y-8 text-center lg:order-1 lg:w-1/2 lg:text-left">
              <h3 className="hidden max-w-fit text-4xl font-semibold lg:block">
                Neem contact op
              </h3>
              <p className="text-muted-foreground lg:max-w-[80%]">
                Heb je vragen? Neem dan gerust contact op met ons team. We nemen
                binnen 48 uur contact met je op.
              </p>
              <div className="space-y-4">
                <h4 className="hidden text-lg font-medium lg:block">
                  Contactgegevens
                </h4>
                <div className="flex flex-col items-center gap-3 lg:items-start">
                  <ContactInfo
                    icon={PhoneIcon}
                    text="085 - 060 0376"
                  />
                  <ContactInfo
                    icon={MailIcon}
                    text="emma@cremma.nl"
                  />
                  {/*
                  <ContactInfo
                    icon={MapPinIcon}
                    text="123 Main St, City, Country"
                  /> */}
                </div>
              </div>
            </div>
            <Card className="order-1 mx-auto w-full py-6 lg:py-10 max-w-lg shadow-lg lg:order-2 lg:w-1/2">
              <CardContent className="flex flex-col gap-6 px-6 lg:px-10">
                <FormProvider {...form}>
                  <form
                    onSubmit={form.handleSubmit(handleSendMessage)}
                    className="space-y-6"
                  >
                    <div className="grid grid-cols-2 gap-4">
                      <FormField
                        control={form.control}
                        name="firstName"
                        render={({ field }) => (
                          <FormItem className="col-span-2 sm:col-span-1">
                            <FormLabel>Voornaam</FormLabel>
                            <FormControl>
                              <Input
                                placeholder="Emma"
                                {...field}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="lastName"
                        render={({ field }) => (
                          <FormItem className="col-span-2 sm:col-span-1">
                            <FormLabel>Achternaam</FormLabel>
                            <FormControl>
                              <Input
                                placeholder="De Planner"
                                {...field}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                    <FormField
                      control={form.control}
                      name="email"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>E-mailadres</FormLabel>
                          <FormControl>
                            <Input
                              type="email"
                              placeholder="emma@cremma.nl"
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="message"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Bericht</FormLabel>
                          <FormControl>
                            <Textarea
                              placeholder="Type je bericht hier."
                              rows={6}
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <Button
                      type="submit"
                      className="w-full"
                      disabled={isExecuting}
                    >
                      {isExecuting ? 'Versturen...' : 'Verstuur bericht'}
                    </Button>
                  </form>
                </FormProvider>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </GridSection>
  );
}

type ContactInfoProps = {
  icon: React.ElementType;
  text: string;
};

function ContactInfo({
  icon: Icon,
  text
}: ContactInfoProps): React.JSX.Element {
  return (
    <div className="flex items-center gap-2 text-sm lg:w-64">
      <Icon className="size-4 shrink-0 text-muted-foreground" />
      <span>{text}</span>
    </div>
  );
}
